/**
 * M7 S35: the mission HUD's models (plan S35; spec §17: no counters anywhere, colour never the only carrier, never
 * say the conclusion). Pure and event-driven (a Career change, a system change, a new `StoryView`): they may
 * allocate; the per-frame HUD only projects what they produced (`hud.ts`).
 *
 * - **Objective** (`objectiveLine`): the one line of the mission in focus: a mission waiting on a fork first (a
 *   decision is pending), else the newest active mission. Its text is `storyHud.json` (`objectives[mission/step]`,
 *   `forks[mission/fork]`); a step with no line shows nothing.
 * - **Markers** (`storyMarkers`): the story sites in the current system that an open lead or the current step points
 *   at, the nav target when it is a story site, and the Tethys story rigs (`StoryView.storyRigs`) in the rig anchor's
 *   system.
 * - **Leads** (`leadsBySystem`): the galaxy map's Leads layer: open log leads and active mission targets by system.
 * - **Sealed items** (`sealedItemLabel`): the cargo view's name for a story item's hold mirror.
 */
import { z } from 'zod'
import type { Career } from '../../career/types'
import type { Vec3T } from '../../galaxy/layout/types'
import type { NavTargetRef } from '../../reach/types'
import type { StorySites } from '../../story-link/sites'
import type { MissionContext } from '../../story/missions'
import { storyOf } from '../../story/state'
import type { StoryMission } from '../../story/types'
import raw from './storyHud.json'

const text = z.string().min(1)

export const storyHudConfigSchema = z.object({
  comment: z.string().optional(),
  objectives: z.record(z.string(), text),
  forks: z.record(z.string(), text),
  objectiveGlyph: text,
  markers: z.object({
    max: z.number().int().min(1).max(16),
    marginPx: z.number().nonnegative(),
    rigMax: z.number().int().nonnegative(),
    rigAnchor: text,
    labels: z.object({ columnNine: text, okaforLab: text, nav: text, rig: text }),
    glyphs: z.object({ site: text, nav: text, rig: text }),
  }),
  leads: z.object({
    id: text,
    label: text,
    leadColor: text,
    missionColor: text,
    leadBadge: text,
    missionBadge: text,
    size: z.number().positive(),
    legendLead: text,
    legendMission: text,
    tooltipLead: text,
    tooltipMission: text,
  }),
  items: z.record(z.string(), text),
  sealed: z.object({ price: text, note: text }),
})
export type StoryHudConfig = z.infer<typeof storyHudConfigSchema>

let cached: StoryHudConfig | null = null
/** storyHud.json, checked once (tests pass their own data). */
export function loadStoryHudConfig(data: unknown = raw): StoryHudConfig {
  if (data !== raw) return storyHudConfigSchema.parse(data)
  cached ??= storyHudConfigSchema.parse(data)
  return cached
}

// ---------------------------------------------------------------------------------------------
// Objective
// ---------------------------------------------------------------------------------------------

/** The mission the HUD speaks for: one awaiting a fork first, else the newest active one. */
export function focusMission(career: Career): StoryMission | null {
  const missions = storyOf(career).missions
  let newest: StoryMission | null = null
  for (const m of missions) {
    if (m.state !== 'active') continue
    if (m.awaitingFork !== undefined) return m
    newest = m
  }
  return newest
}

/** The one-line objective of `m` (no counters), or null when it has no line. */
export function missionObjective(m: StoryMission, cfg: StoryHudConfig = loadStoryHudConfig()): string | null {
  if (m.state !== 'active') return null
  if (m.awaitingFork !== undefined) return cfg.forks[`${m.id}/${m.awaitingFork}`] ?? null
  return cfg.objectives[`${m.id}/${m.step}`] ?? null
}

/** The HUD's objective line ('' = hidden): the glyph, then the focus mission's line. */
export function objectiveLine(career: Career, cfg: StoryHudConfig = loadStoryHudConfig()): string {
  const m = focusMission(career)
  const line = m === null ? null : missionObjective(m, cfg)
  return line === null ? '' : `${cfg.objectiveGlyph} ${line}`
}

/** A counter in a HUD line ("2/3", "3 left", "1 of 4", any digit): spec §17 forbids them. */
export function hasCounter(line: string): boolean {
  return /\d/.test(line)
}

// ---------------------------------------------------------------------------------------------
// Markers
// ---------------------------------------------------------------------------------------------

export type StoryMarkerKind = 'site' | 'nav' | 'rig'

export interface StoryMarker {
  key: string
  kind: StoryMarkerKind
  /** Glyph + label: never colour alone. */
  label: string
  position: Vec3T
}

/** What `storyMarkers` reads. */
export interface StoryMarkerInput {
  career: Career
  missions: MissionContext
  /** The lead log → its system hint (`systemHint`), for the open leads. */
  leadHint: (logId: string) => string | undefined
  sites: StorySites | null
  systemId: string | null
  navTarget: NavTargetRef | null
  /** `StoryView.storyRigs` (0 before the ramp). */
  rigs: number
  /** The rig anchor's system id (null when the galaxy has none). */
  rigSystemId: string | null
  /** The i-th rig's position in the rig system. */
  rigPoint: (i: number) => Vec3T
}

const SITE_KEYS = ['column-nine', 'okafor-lab'] as const
type SiteKey = (typeof SITE_KEYS)[number]

/** The site keys an open lead or an active mission's current step points at. */
export function pointedSites(career: Career, missions: MissionContext, leadHint: (logId: string) => string | undefined): Set<SiteKey> {
  const out = new Set<SiteKey>()
  const story = storyOf(career)
  const add = (ref: string | undefined): void => {
    for (const k of SITE_KEYS) if (ref === k) out.add(k)
  }
  for (const id of story.leads) add(leadHint(id))
  for (const m of story.missions) {
    if (m.state !== 'active' || m.awaitingFork !== undefined) continue
    add(missions.missions[m.id].steps.find((s) => s.id === m.step)?.target.ref)
  }
  return out
}

/** The markers of the current system, at most `markers.max`. */
export function storyMarkers(input: StoryMarkerInput, cfg: StoryHudConfig = loadStoryHudConfig()): StoryMarker[] {
  const out: StoryMarker[] = []
  const sys = input.systemId
  if (sys === null) return out
  const mc = cfg.markers
  const pointed = pointedSites(input.career, input.missions, input.leadHint)
  const nav = input.navTarget
  const sites = input.sites
  if (sites !== null) {
    const list: readonly [SiteKey, StorySites['columnNine'], string][] = [
      ['column-nine', sites.columnNine, mc.labels.columnNine],
      ['okafor-lab', sites.okaforLab, mc.labels.okaforLab],
    ]
    for (const [key, site, label] of list) {
      if (site.systemId !== sys) continue
      const isNav = nav !== null && nav.id === site.poiId
      if (!isNav && !pointed.has(key)) continue
      out.push(
        isNav
          ? { key, kind: 'nav', label: `${mc.glyphs.nav} ${mc.labels.nav}: ${label}`, position: site.position }
          : { key, kind: 'site', label: `${mc.glyphs.site} ${label}`, position: site.position },
      )
    }
  }
  if (input.rigs > 0 && input.rigSystemId === sys) {
    const n = Math.min(input.rigs, mc.rigMax)
    for (let i = 0; i < n; i++) out.push({ key: `rig:${i}`, kind: 'rig', label: `${mc.glyphs.rig} ${mc.labels.rig}`, position: input.rigPoint(i) })
  }
  return out.slice(0, mc.max)
}

// ---------------------------------------------------------------------------------------------
// The Leads map layer
// ---------------------------------------------------------------------------------------------

export interface LeadsAtSystem {
  /** Lead log titles. */
  leads: string[]
  /** Mission objectives whose target is here. */
  missions: string[]
}

export interface LeadsInput {
  career: Career
  missions: MissionContext
  lead: (logId: string) => { title: string; systemHint: string } | undefined
  /** A story hint or anchor → system id (the story service's `resolveSystem`). */
  resolve: (hint: string) => string | null
}

/** Open leads and active mission targets, by system (unresolved hints are left out: "no fix yet"). */
export function leadsBySystem(input: LeadsInput, cfg: StoryHudConfig = loadStoryHudConfig()): Map<string, LeadsAtSystem> {
  const out = new Map<string, LeadsAtSystem>()
  const at = (sys: string): LeadsAtSystem => {
    let e = out.get(sys)
    if (e === undefined) {
      e = { leads: [], missions: [] }
      out.set(sys, e)
    }
    return e
  }
  const story = storyOf(input.career)
  for (const id of story.leads) {
    const l = input.lead(id)
    const sys = l === undefined ? null : input.resolve(l.systemHint)
    if (l !== undefined && sys !== null && !at(sys).leads.includes(l.title)) at(sys).leads.push(l.title)
  }
  for (const m of story.missions) {
    if (m.state !== 'active' || m.awaitingFork !== undefined) continue
    const step = input.missions.missions[m.id].steps.find((s) => s.id === m.step)
    const objective = missionObjective(m, cfg)
    const sys = step === undefined ? null : input.resolve(step.target.ref)
    if (sys !== null && objective !== null) at(sys).missions.push(objective)
  }
  return out
}

export interface LeadsNodeStyle {
  color: string
  size: number
  badge: string
}

/** A system's Leads style: the badge always says which (colour is never the only carrier). */
export function leadsNodeStyle(e: LeadsAtSystem | undefined, cfg: StoryHudConfig = loadStoryHudConfig()): LeadsNodeStyle | null {
  if (e === undefined || (e.leads.length === 0 && e.missions.length === 0)) return null
  const l = cfg.leads
  const mission = e.missions.length > 0
  const lead = e.leads.length > 0
  return { color: mission ? l.missionColor : l.leadColor, size: l.size, badge: `${mission ? l.missionBadge : ''}${lead ? l.leadBadge : ''}` }
}

/** The tooltip lines of a system on the Leads layer. */
export function leadsTooltip(e: LeadsAtSystem | undefined, cfg: StoryHudConfig = loadStoryHudConfig()): string[] {
  if (e === undefined) return []
  const out: string[] = []
  for (const o of e.missions) out.push(cfg.leads.tooltipMission.replace('{objective}', o))
  for (const t of e.leads) out.push(cfg.leads.tooltipLead.replace('{title}', t))
  return out
}

// ---------------------------------------------------------------------------------------------
// Sealed items (the cargo view)
// ---------------------------------------------------------------------------------------------

/** The cargo-view name of a story item (the id after the mirror's `story:` prefix). */
export function sealedItemLabel(itemId: string, cfg: StoryHudConfig = loadStoryHudConfig()): string {
  return cfg.items[itemId] ?? itemId
}
